import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

interface ArticleSection {
  heading: string;
  paragraphs: string[];
}

export interface ArticleData {
  label: string;
  title: string;
  subtitle: string;
  image: string;
  imageAlt: string;
  sections: ArticleSection[];
  ctaText: string;
  ctaUrl: string;
}

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuy.skin';

function buildArticleSchema(data: ArticleData, pathname: string) {
  const allText = data.sections.map(s => s.heading + ' ' + s.paragraphs.join(' ')).join(' ');
  const wordCount = allText.split(/\s+/).length;
  const readMinutes = Math.max(1, Math.ceil(wordCount / 200));

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: data.title,
    description: data.subtitle,
    image: data.image,
    datePublished: '2026-06-28',
    dateModified: '2026-06-28',
    author: {
      '@type': 'Organization',
      name: 'Itaobuy Sheet Team',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Itaobuy Sheet',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}${pathname}`,
    },
    wordCount,
    timeRequired: `PT${readMinutes}M`,
    inLanguage: 'en',
  };
}

const breadcrumbSchema = (pathname: string, title: string) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: title.length > 50 ? title.slice(0, 50) + '...' : title,
      item: `${siteUrl}${pathname}`,
    },
  ],
});

export default function ArticleLayout({ data }: { data: ArticleData }) {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <>
      <JsonLd data={buildArticleSchema(data, pathname)} />
      <JsonLd data={breadcrumbSchema(pathname, data.title)} />
      <Navbar />
      <main>
        <article className="pt-24 pb-12 px-6 md:px-10">
          <div className="max-w-[800px] mx-auto">
            <nav className="mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm text-foreground-500">
                <li>
                  <Link to="/" className="hover:text-foreground-800 transition-colors duration-150">
                    Home
                  </Link>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </span>
                  <span className="text-foreground-800">{data.title.length > 50 ? data.title.slice(0, 50) + '...' : data.title}</span>
                </li>
              </ol>
            </nav>

            <header className="mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
                {data.label}
              </span>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                {data.title}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base leading-relaxed">
                {data.subtitle}
              </p>
              <div className="flex items-center gap-4 mt-6 pt-6 border-t border-background-200/60">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                  <span className="text-accent-700 font-bold text-sm">IS</span>
                </div>
                <div>
                  <p className="text-foreground-800 text-sm font-medium">Itaobuy Sheet Team</p>
                  <p className="text-foreground-400 text-xs">Updated June 2026 &middot; 6 min read</p>
                </div>
              </div>
            </header>

            <div className="relative w-full h-[350px] md:h-[420px] rounded-2xl overflow-hidden mb-10">
              <img
                src={data.image}
                alt={data.imageAlt}
                title={data.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="text-foreground-700 leading-relaxed space-y-6">
              {data.sections.map((section, idx) => (
                <section key={idx}>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-10 mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((p, pidx) => (
                    <p key={pidx} className="text-sm md:text-base leading-relaxed mt-4" dangerouslySetInnerHTML={{ __html: p }} />
                  ))}
                </section>
              ))}

              <section className="bg-accent-50 rounded-2xl p-6 md:p-8 mt-10">
                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                  Ready to Try the Itaobuy Spreadsheet?
                </h3>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-5">
                  The itaobuy spreadsheet system is available now on the main platform. Browse 14 product categories and start sourcing today.
                </p>
                <a
                  href={data.ctaUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                >
                  {data.ctaText}
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line"></i>
                  </span>
                </a>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/60">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-foreground-600 text-sm hover:text-foreground-900 transition-colors duration-150"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </span>
                Back to Homepage
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}